// src/components/media360/ui/UploadExperienceForm.jsx
import React, { useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Icon,
  Input,
  Message,
  Progress,
} from "semantic-ui-react";

const MEDIA_KIND_OPTIONS = [
  { key: "video", text: "Vídeo", value: "video" },
  { key: "image", text: "Imagen", value: "image" },
];

const STEREO_TYPE_OPTIONS = [
  { key: "360", text: "360 (Mono)", value: "360" },
  { key: "TB", text: "3D Top-Bottom (TB)", value: "TB" },
  { key: "LR", text: "3D Side-by-Side (LR)", value: "LR" },
];

export function UploadExperienceForm({
  uploading,
  disabled,
  uploadProgress,
  uploadProgressKnown,
  error,
  onUpload, // async ({ mediaFile, mediaKind, title, stereoType, loop, audioFile? }) => result
  onUploaded, // (result) => void
}) {
  const [mediaKind, setMediaKind] = useState("video");
  const [stereoType, setStereoType] = useState("360");
  const [title, setTitle] = useState("");
  const [loop, setLoop] = useState(false);
  const [mediaFile, setMediaFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);

  const acceptMedia = useMemo(() => {
    return mediaKind === "image"
      ? "image/jpeg,image/png,image/webp"
      : "video/mp4,video/webm";
  }, [mediaKind]);

  const acceptAudio =
    "audio/mpeg,audio/mp3,audio/ogg,audio/wav,audio/mp4,audio/aac";

  const canSubmit =
    Boolean(title?.trim()) && Boolean(mediaFile) && !disabled && !uploading;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    const res = await onUpload({
      mediaFile,
      mediaKind,
      title: title.trim(),
      stereoType,
      loop,
      audioFile: audioFile || undefined,
    });

    onUploaded?.(res);

    // Limpieza mínima (no tocamos title por si quieres subir variantes)
    setMediaFile(null);
    setAudioFile(null);
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit();
      }}
    >
      <Form.Field>
        <label>Título (base del nombre)</label>
        <Input
          placeholder="Ej: domingoramos-pasion"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={disabled || uploading}
        />
      </Form.Field>

      <Form.Group widths="equal">
        <Form.Field>
          <label>Media</label>
          <Dropdown
            selection
            options={MEDIA_KIND_OPTIONS}
            value={mediaKind}
            onChange={(_, data) => setMediaKind(data.value)}
            disabled={disabled || uploading}
          />
        </Form.Field>

        <Form.Field>
          <label>Modo</label>
          <Dropdown
            selection
            options={STEREO_TYPE_OPTIONS}
            value={stereoType}
            onChange={(_, data) => setStereoType(data.value)}
            disabled={disabled || uploading}
          />
        </Form.Field>

        <Form.Field style={{ display: "flex", alignItems: "flex-end" }}>
          <Checkbox
            label="Loop infinito"
            checked={loop}
            onChange={(_, data) => setLoop(Boolean(data.checked))}
            disabled={disabled || uploading}
          />
        </Form.Field>
      </Form.Group>

      <Form.Field>
        <label>
          {mediaKind === "image" ? "Archivo de imagen" : "Archivo de vídeo"}
        </label>
        <Input
          type="file"
          accept={acceptMedia}
          onChange={(e) => setMediaFile(e.target.files?.[0] || null)}
          disabled={disabled || uploading}
        />
      </Form.Field>

      <Form.Field>
        <label>Audio adicional (opcional)</label>
        <Input
          type="file"
          accept={acceptAudio}
          onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
          disabled={disabled || uploading}
        />
        <div style={{ marginTop: 6, fontSize: 12, color: "#666" }}>
          Para imágenes 360 suele ser lo normal. En vídeo, normalmente no hace
          falta (ya lleva audio embebido).
        </div>
      </Form.Field>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <pre style={{ whiteSpace: "pre-wrap" }}>
            {String(error?.message || error)}
          </pre>
        </Message>
      )}

      <Button primary fluid type="submit" disabled={!canSubmit}>
        <Icon name="upload" /> Subir
      </Button>

      {uploading && (
        <div style={{ marginTop: 12 }}>
          <Progress
            percent={uploadProgressKnown ? uploadProgress : undefined}
            indicating
            progress={uploadProgressKnown ? "percent" : false}
            label={
              uploadProgressKnown ? `Subiendo… ${uploadProgress}%` : "Subiendo…"
            }
          />
        </div>
      )}
    </Form>
  );
}
